import React from 'react';
import {
  Table,
  TableCell,
  TableBody,
  TableRow,
  TableHead,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Heading from 'components/Heading';

const Note = styled('div')(({ theme }) => ({
  fontSize: 16,
  color: '#656565',
  marginBottom: theme.spacing(6),
}));

const StyledTableHead = styled(TableHead)({
  backgroundColor: 'rgba(234, 233, 232, .2)',
});

const StyledHeadCell = styled(TableCell)({
  minWidth: 120,
  borderBottomColor: '#EDEDED',
  lineHeight: '22px',
  fontSize: 12,
  fontWeight: 600,
  fontStyle: 'normal',
});

const StyledBodyCell = styled(TableCell)({
  borderBottomColor: '#EDEDED',
});

type ErrorRow = {
  rowId: number;
  rowFullName: string;
  message: string;
  displayRowId?: number;
};

type GroupedErrorRow = {
  rowId: number;
  displayRowId?: number;
  rowFullName: string;
  messages: Array<string>;
};

type Props = {
  errors: Array<ErrorRow>;
  title?: string;
  description?: string;
};

const groupErrors = (errors: Array<ErrorRow>): Array<GroupedErrorRow> => {
  const grouped = new Map<number, GroupedErrorRow>();

  errors.forEach((error) => {
    const existing = grouped.get(error.rowId);
    if (existing) {
      existing.messages.push(error.message);
      return;
    }
    grouped.set(error.rowId, {
      rowId: error.rowId,
      displayRowId: error.displayRowId,
      rowFullName: error.rowFullName,
      messages: [error.message],
    });
  });

  return Array.from(grouped.values());
};

const ErrorsTableV2: React.FC<Props> = ({ errors, title, description }) => {
  const groupedErrors = groupErrors(errors);

  return (
    <>
      <Grid size={{ md: 12 }} className="mt-8">
        <Heading variant="h2">{title || 'Validation error'}</Heading>
        <Note>
          {description ||
            'The required fields have not been entered correctly above. Please see below for more information and try again.'}
        </Note>
        <Table>
          <StyledTableHead>
            <TableRow>
              <StyledHeadCell>Row number</StyledHeadCell>
              <StyledHeadCell>Name</StyledHeadCell>
              <StyledHeadCell>Errors</StyledHeadCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {groupedErrors.map((row) => {
              const displayRowId =
                row.displayRowId ??
                (Number.isNaN(row.rowId) ? '' : row.rowId + 1);
              return (
                <TableRow key={`${row.rowId}-${displayRowId}`}>
                  <StyledBodyCell>{displayRowId}</StyledBodyCell>
                  <StyledBodyCell>{row.rowFullName}</StyledBodyCell>
                  <StyledBodyCell>
                    <ul>
                      {row.messages.map((message, index) => (
                        <li key={`${row.rowId}-${index}`}>{message}</li>
                      ))}
                    </ul>
                  </StyledBodyCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Grid>
    </>
  );
};

export default ErrorsTableV2;
